import { Injectable, OnDestroy } from '@angular/core';
import { catchError, filter, map, tap } from 'rxjs/operators';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject, Observable, ReplaySubject, take, throwError } from 'rxjs';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Subscription } from 'rxjs/internal/Subscription';
import { CookieService } from 'ngx-cookie-service';
import { JwtUser } from '../dto/jwt-user';
import { UserPermission } from '../enums/user-permission.enum';
import { Router } from '@angular/router';
import { UserReadableApiV2Error } from '../../../api/errors/user-readable-api-v2.error';
import { BackendErrorTranslatorService } from '../../../services/localization/backend-error-translator.service';
import { InitiateSsoParams } from '../types/initiate-sso-params.type';

@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    public static CsrfProtectionHeader = 'X-MDR-CSRF-PROTECTION';

    public jwtUser: JwtUser | null = null;

    private jwtUserSubj = new ReplaySubject<JwtUser>(1);
    private jwtUser$: Observable<JwtUser> = this.jwtUserSubj.asObservable();
    private accessTokenSubj = new ReplaySubject<string>(1);
    private atSub: Subscription;

    private isRefreshingTokens = new BehaviorSubject<boolean>(false);
    private hasLoadedTokenFromCookie = false;
    private httpClient: HttpClient;

    private hasInitiatedSso = false;

    private readonly ExpiryThreshold = 60; // seconds

    constructor(
        private cookieService: CookieService,
        private router: Router,
        private backendErrorTranslator: BackendErrorTranslatorService,
        httpBackend: HttpBackend,
    ) {
        // we don't want any interceptors for the auth service
        this.httpClient = new HttpClient(httpBackend);

        if (this.hasAccessToken()) {
            this.loadAccessTokenFromCookies();
        } else if (window.location.pathname.includes('sign-up') || window.location.pathname.includes('login')) {
            // this.jwtUser = this.getJwtUserFromSignUpToken();
            // this.jwtUserSubj.next(this.jwtUser);
        }

        window.addEventListener('pageshow', this.onPageShow.bind(this));
    }

    public ngOnDestroy(): void {
        this.atSub?.unsubscribe();

        window.removeEventListener('pageshow', this.onPageShow.bind(this));
    }

    public get isLoggedIn() {
        return this.jwtUser !== null;
    }

    public navigateToDiscoveryPage() {
        localStorage.setItem('redirectToInitialUrl', 'true');
        this.router.navigate(['/discovery']).catch(console.error);
    }

    public initiateMdrDiscovery(email: string): Observable<{ idpRegion: string | null }> {
        return this.httpClient
            .post<{ idpRegion: string | null }>('/v2/users/sso/mdr-discovery', { email })
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public getGravityZoneIdpRegion(email: string): Observable<{ idpRegion: string | null }> {
        return this.httpClient
            .post<{ idpRegion: string | null }>('/v2/users/sso/get-gravityzone-region', { email })
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public initiateSsoLogin(options: InitiateSsoParams = {}) {
        if (this.hasInitiatedSso) {
            return;
        }

        this.hasInitiatedSso = true;
        localStorage.setItem('redirectToInitialUrl', 'true');

        const queryParams: Record<string, string> = {};

        if (options.idpRegion !== null && options.idpRegion !== undefined) {
            queryParams['idpRegion'] = options.idpRegion;
        }

        if (options.metadataUrl !== null && options.metadataUrl !== undefined) {
            queryParams['metadataUrl'] = options.metadataUrl;
        }

        if (options.email !== null && options.email !== undefined) {
            queryParams['email'] = options.email;
        }

        const queryString = new URLSearchParams(queryParams).toString();

        window.location.href = `/v2/users/sso/initiate-sso-login?${queryString}`;
    }

    public hasAccessToken(): boolean {
        return this.cookieService.check('mdr_csrf_token') && this.cookieService.check('mdr_access_token');
    }

    /** refresh_token cannot be read from JS, so we only read CSRF which is set with the same expiry as refresh_token **/
    public hasRefreshToken(): boolean {
        return this.cookieService.check('mdr_csrf_token');
    }

    public shouldRefreshJwtTokens(): boolean {
        if (this.jwtUser === null || this.jwtUser === undefined || this.jwtUser.Exp === null || this.jwtUser.Exp === undefined) {
            return this.cookieService.check('mdr_csrf_token');
        }

        return this.jwtUser.Exp - Date.now() / 1000 < this.ExpiryThreshold;
    }

    public refreshTokens(): Observable<boolean> {
        if (this.hasInitiatedSso) {
            return throwError(() => new Error('Cannot refresh after initiating SSO auth'));
        }

        const csrfToken = this.cookieService.get('mdr_csrf_token');
        const headers = new HttpHeaders({ 'X-MDR-CSRF-PROTECTION': csrfToken });

        if (!this.isRefreshingTokens.value) {
            // first http request will make a post to /refresh-tokens
            this.isRefreshingTokens.next(true);

            this.httpClient
                .post<void>('/v2/users/sso/refresh-tokens', {}, { headers })
                .pipe(
                    tap(() => {
                        this.accessTokenSubj.next(this.getAccessToken());
                        this.loadAccessTokenFromCookies();
                    }),
                    map(() => {
                        return false;
                    }),
                )
                .subscribe({
                    error: () => {
                        // for errors on refresh, redirect to SSO login
                        const idpRegion = this.cookieService.get('mdr_idp_region') ?? null;

                        if (idpRegion === null || idpRegion === '') {
                            this.navigateToDiscoveryPage();
                        } else {
                            this.initiateSsoLogin({ idpRegion });
                        }

                        this.isRefreshingTokens.next(false);
                    },
                    complete: () => {
                        this.isRefreshingTokens.next(false);
                    },
                });
        }

        // http requests will wait for the refresh-tokens request to end, then continue
        return this.isRefreshingTokens.asObservable().pipe(
            filter((value) => !value),
            take(1),
        );
    }

    public logout(): void {
        const csrfToken = this.cookieService.get('mdr_csrf_token');
        const headers = new HttpHeaders({ 'X-MDR-CSRF-PROTECTION': csrfToken });

        this.httpClient
            .post<{ url: string }>(
                '/v2/users/sso/initiate-sso-logout',
                {},
                {
                    headers,
                },
            )
            .subscribe({
                next: (response: { url: string }) => {
                    this.clearStorage();
                    window.location.href = response.url;
                },
                error: () => {
                    this.clearStorage();
                    this.navigateToDiscoveryPage();
                },
            });
    }

    public isLogged(): boolean {
        return this.isLoggedIn;
    }

    public hasAtLeastOneRole$(requiredRoles: UserPermission[]): Observable<boolean> {
        return this.jwtUser$.pipe(
            // we only care about current user logged in
            take(1),
            map((user: JwtUser): boolean => {
                return user.Permissions.filter((role) => requiredRoles.includes(role)).length > 0;
            }),
        );
    }

    public hasAtLeastOneRole(requiredRoles: UserPermission[]): boolean {
        return this.jwtUser !== null && this.jwtUser.Permissions.filter((role) => requiredRoles.includes(role)).length > 0;
    }

    public loadAccessTokenFromCookies() {
        if (this.hasLoadedTokenFromCookie) {
            return;
        }

        this.hasLoadedTokenFromCookie = true;

        this.atSub = this.accessTokenSubj.subscribe({
            next: (accessToken) => {
                this.jwtUser = new JwtUser(jwtDecode(accessToken));
                this.jwtUserSubj.next(this.jwtUser);
            },
            error: (error) => {
                console.error('error on reading access token', error);
                return this.navigateToDiscoveryPage();
            },
        });

        this.accessTokenSubj.next(this.getAccessToken());
    }

    public clearStorage(): void {
        /** clear everything EXCEPT isPanelExpanded */
        const isSidenavExpanded = localStorage.getItem('isSidenavExpanded');

        localStorage.clear();
        localStorage.setItem('isPanelExpanded', isSidenavExpanded + '');
        localStorage.removeItem('isPanelExpanded');
        localStorage.removeItem('company');
        localStorage.removeItem('isSidenavExpanded');
        localStorage.removeItem('acceptedTC');

        this.cookieService.delete('mdr_csrf_token');
        this.cookieService.delete('mdr_access_token');
        this.cookieService.delete('mdr_refresh_token');
    }

    public hasRoles(requiredRoles: UserPermission[]): boolean {
        return requiredRoles.every((role) => this.jwtUser?.Permissions.includes(role));
    }

    private getAccessToken(): string {
        return this.cookieService.get('mdr_access_token');
    }

    private getJwtUserFromSignUpToken(): JwtUser {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const tokenFromUrl = urlParams.get('token') || '';

        return new JwtUser(jwtDecode(tokenFromUrl));
    }

    private convertToUserReadableError(httpError: HttpErrorResponse): Observable<any> {
        return throwError(() => new UserReadableApiV2Error(this.backendErrorTranslator, httpError));
    }

    private onPageShow(event: PageTransitionEvent): void {
        if (!event.persisted) {
            return;
        }

        // The page was restored from bfcache (e.g., user navigated back from another site)
        this.hasInitiatedSso = false;
    }
}
